/**
 * Shared date handling for the ministry/renewal screens.
 *
 * WHY THIS EXISTS: `new Date('2026-07-06')` parses a date-only string as UTC
 * midnight, so rendering it in any negative-offset timezone (all of the US)
 * shows the PREVIOUS day — e.g. 7/5/2026. Every renewal screen had its own
 * copy of this parsing, and they did not agree. Parse the parts explicitly and
 * build a LOCAL date instead. Use these helpers rather than `new Date(str)`.
 */

/** Parse 'YYYY-MM-DD' (or an ISO datetime) as a LOCAL date. Null if unusable. */
export function parseDateOnly(dateString) {
  if (!dateString || dateString === '0000-00-00') return null;
  const [y, m, d] = String(dateString).split('T')[0].split(' ')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

/** 'YYYY-MM-DD' for form inputs. Falls back to today when asked (input needs a value). */
export function toIsoDate(dateString, { fallbackToday = false } = {}) {
  const dt = parseDateOnly(dateString) ?? (fallbackToday ? new Date() : null);
  if (!dt) return '';

  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return [dt.getFullYear(), month, day].join('-');
}

/** Localized display date, or '' when there is no usable date. */
export function toLocaleDate(dateString, lang) {
  const dt = parseDateOnly(dateString);
  return dt ? dt.toLocaleDateString(lang) : '';
}
